// feat_316 — cálculo de la próxima apertura de la empresa.
//
// Forma real del setting `businessHours` (whaticket.Settings, JSON-en-TEXT):
//   {"monday":[{"open":"08:00","close":"18:00"}], ..., "sunday":[]}
// Array de tramos por día (soporta corte de mediodía). Claves en inglés minúscula.
// Un día puede faltar o venir vacío ([]) → la empresa NO abre ese día.

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

/**
 * Parseo tolerante del setting. Devuelve el objeto o null (nunca lanza).
 * @param {string|object|null} raw
 * @returns {object|null}
 */
export const parseBusinessHours = raw => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Calcula el próximo instante de apertura de la empresa desde `from`.
 *
 * Recorre día por día (máx. 7 iteraciones) buscando el primer tramo cuyo `open`
 * sea posterior a `from`. Salta días vacíos o ausentes — NO asume que mañana abre.
 *
 * Cross-midnight: un tramo {"open":"22:00","close":"02:00"} es representable,
 * pero el algoritmo SOLO mira `open` → no se confunde con el `close` del día siguiente.
 *
 * @param {Date} from - instante desde el cual buscar
 * @param {object|null} businessHours - {monday:[{open,close}], ...} ya parseado
 * @param {string|null} timezone - IANA tz de la empresa (v1: informativa, ver nota)
 * @param {boolean} always247 - true = la empresa nunca cierra → null
 * @returns {Date|null} - null si no se puede calcular (no ofrecer la opción)
 */
export const nextBusinessOpening = (from, businessHours, timezone, always247) => {
  if (always247) return null;
  if (!businessHours || typeof businessHours !== "object") return null;
  if (!(from instanceof Date) || isNaN(from.getTime())) return null;

  const cursor = new Date(from);

  // Tope de 7 días: cubre la semana completa y acota un businessHours mal formado.
  for (let i = 0; i < 7; i++) {
    const ranges = businessHours[DAY_NAMES[cursor.getDay()]];

    if (Array.isArray(ranges)) {
      for (const range of ranges) {
        if (!range || typeof range.open !== "string") continue;
        const [h, m] = range.open.split(":").map(Number);
        if (!Number.isFinite(h) || !Number.isFinite(m)) continue;

        const openDate = new Date(cursor);
        openDate.setHours(h, m, 0, 0);

        if (openDate > from) return openDate;
      }
    }

    // Siguiente día a las 00:00 (el primer tramo del día ya cuenta entero)
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(0, 0, 0, 0);
  }

  return null; // sin apertura en los próximos 7 días
};

/**
 * ¿Se puede ofrecer "silenciar hasta que abramos"? Fail-safe: sin businessHours
 * o con always247 la opción NO se ofrece (no inventar una apertura que no existe).
 * @param {string|object|null} rawBusinessHours
 * @param {boolean} always247
 */
export const canComputeNextOpening = (rawBusinessHours, always247) => {
  if (always247) return false;
  const parsed = parseBusinessHours(rawBusinessHours);
  if (!parsed) return false;
  // Al menos un día con un tramo con `open` → si no, nunca abre.
  return DAY_NAMES.some(
    d => Array.isArray(parsed[d]) && parsed[d].some(r => r && r.open)
  );
};

// ⚠ v1 usa la tz del NAVEGADOR para construir el Date de apertura. Todas las
// empresas actuales están en Argentina y sus usuarios también, así que coincide.
// Para una empresa cuyo horario esté en otra tz que la del usuario (Italia co8),
// habría que resolver el offset con `formatToParts` como en feat_315 — el
// parámetro `timezone` ya está en la firma para no cambiarla cuando toque.
